import propz from 'propz';
import moment from 'moment';

export type AddMode = 'ELIGIBLE_ONLY' | 'SHOW_ALL';

export const normId = (v: any) => String(v || '');

export const getErrMsg = (e: any, fallback: string) => {
  try {
    return (e && e.response && e.response.data && e.response.data.error) || e.message || fallback;
  } catch {
    return fallback;
  }
};

export const toYMD = (iso?: string) => (iso ? String(iso).slice(0, 10) : '');

export const fmtDMY = (iso?: string) => {
  const ymd = toYMD(iso);
  if (!ymd) return '';
  const y = ymd.slice(0, 4);
  const m = ymd.slice(5, 7);
  const d = ymd.slice(8, 10);
  return `${d}/${m}/${y}`;
};

export const getClubId = (c: any) => String((c && (c.id || c._id)) || '');

export const getClubSportName = (c: any) => String(propz.get(c, ['sport', 'name'], '') || '');

export const getClubDay = (c: any) => String(propz.get(c, ['schedule', 'days', 0], '') || '');

export const getIntervals = (c: any) => {
  const ints = propz.get(c, ['schedule', 'intervals'], []);
  return Array.isArray(ints) ? ints : [];
};

export const getDerivedStartIsoFromIntervals = (c: any) => {
  const ints = getIntervals(c);
  const times = ints.map(x => new Date(propz.get(x, ['start'], '')).getTime()).filter(t => Number.isFinite(t));
  if (!times.length) return '';
  return new Date(Math.min(...times)).toISOString();
};

export const getDerivedFinishIsoFromIntervals = (c: any) => {
  const ints = getIntervals(c);
  const times = ints.map(x => new Date(propz.get(x, ['end'], '')).getTime()).filter(t => Number.isFinite(t));
  if (!times.length) return '';
  return new Date(Math.max(...times)).toISOString();
};

export const getClubStartIso = (c: any) => {
  const direct = String(propz.get(c, ['schedule', 'startDate'], '') || '');
  return direct || getDerivedStartIsoFromIntervals(c);
};

export const getClubFinishIso = (c: any) => {
  const direct = String(propz.get(c, ['schedule', 'finishDate'], '') || '');
  return direct || getDerivedFinishIsoFromIntervals(c);
};

export const isOneDayClub = (c: any) => {
  const days = propz.get(c, ['schedule', 'days'], []);
  return Array.isArray(days) && days.length === 1;
};

export const isStartedOrPast = (c: any, nowMs: number = Date.now()) => {
  const iso = getClubStartIso(c);
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t <= nowMs : false;
};

export const isFinishedOrPast = (c: any, nowMs: number = Date.now()) => {
  const iso = getClubFinishIso(c);
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t <= nowMs : false;
};

export const isClubRemoved = (club: any) => !!club?.isRemoved || !!club?.isDeleted || !!club?.deletedAt;

type BuildAddWhereParams = {
  addMode: AddMode;

  addSportIds: string[];
  addDays: string[];
  addGenders: string[];
  addAgeValues: number[];

  addStartFrom: string;
  addStartTo: string;
  addSearch: string;

  excludeClubIds: string[];
  nowIso?: string;
};

export const buildAddWhere = ({
  addMode,
  addSportIds,
  addDays,
  addGenders,
  addAgeValues,
  addStartFrom,
  addStartTo,
  addSearch,
  excludeClubIds,
  nowIso
}: BuildAddWhereParams) => {
  const where: any = {};
  const now = nowIso || new Date().toISOString();

  if (addSportIds?.length) where.sportId = { $in: addSportIds };
  if (addGenders?.length) where.gender = { $in: addGenders };
  if (addAgeValues?.length) where.ages = { $in: addAgeValues };

  if (addDays?.length) {
    where['schedule.days.0'] = { $in: addDays };
  } else {
    where['schedule.days.0'] = { $exists: true };
  }
  where['schedule.days.1'] = { $exists: false };

  if (addStartFrom || addStartTo) {
    const range: any = {};
    if (addStartFrom) range.$gte = new Date(`${addStartFrom}T00:00:00.000Z`).toISOString();
    if (addStartTo) range.$lte = new Date(`${addStartTo}T23:59:59.999Z`).toISOString();
    where['schedule.startDate'] = range;
  }

  const q = (addSearch || '').trim();
  if (q) where.name = { $regex: q, $options: 'i' };

  where.status = 'DRAFT';

  if (excludeClubIds?.length) where._id = { $nin: excludeClubIds };

  where.$and = where.$and || [];
  where.$and.push({
    $or: [
      { 'schedule.finishDate': { $gt: now } },

      {
        $and: [{ 'schedule.scheduleType': 'INTERVALS' }, { 'schedule.intervals.end': { $gt: now } }]
      },

      {
        $and: [{ 'schedule.finishDate': { $exists: false } }, { 'schedule.scheduleType': { $ne: 'INTERVALS' } }]
      }
    ]
  });

  if (addMode === 'ELIGIBLE_ONLY') {
    where.$and.push({
      $or: [
        { 'schedule.startDate': { $gt: now } },
        { $and: [{ 'schedule.scheduleType': 'INTERVALS' }, { 'schedule.intervals.start': { $gt: now } }] }
      ]
    });
  }

  return where;
};

export const mapClubProgrammeWhereUiToServer = (whereIn: any) => {
  const where: any = { ...(whereIn || {}) };

  if (where.preBookingAccess) {
    where['booking.preBookingAccess'] = where.preBookingAccess;
    delete where.preBookingAccess;
  }

  if (where.bookingOpensAt) {
    where['booking.opensAt'] = where.bookingOpensAt;
    delete where.bookingOpensAt;
  }

  if (where.bookingClosesAt) {
    where['booking.closesAt'] = where.bookingClosesAt;
    delete where.bookingClosesAt;
  }

  if (where.selectionMode) {
    where['selectionRules.mode'] = where.selectionMode;
    delete where.selectionMode;
  }

  return where;
};

export const mapClubProgrammeSortUiToServer = (field: string) => {
  switch (field) {
    case 'bookingOpensAt':
      return 'booking.opensAt';
    case 'bookingClosesAt':
      return 'booking.closesAt';
    case 'preBookingAccess':
      return 'booking.preBookingAccess';
    case 'selectionMode':
      return 'selectionRules.mode';
    default:
      return field;
  }
};

export const clubProgrammeNormalizeCount = (obj: any): number => {
  const c = propz.get(obj, ['count'], 0);
  if (typeof c === 'number') return c;
  if (typeof c === 'string') {
    const n = Number(c);
    return Number.isFinite(n) ? n : 0;
  }
  if (c && typeof c === 'object') {
    const inner = propz.get(c, ['count'], 0);
    if (typeof inner === 'number') return inner;
    if (typeof inner === 'string') {
      const n = Number(inner);
      return Number.isFinite(n) ? n : 0;
    }
  }
  return 0;
};

export const clubProgrammeParseUiDate = (v: any): moment.Moment | null => {
  const s = String(v || '').trim();
  if (!s) return null;

  const m = moment(s, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return m.isValid() ? m : null;
};

export const clubProgrammeNormalizeWhereValue = (value: any) => {
  if (!value || typeof value !== 'object') return value;

  if (value.like && typeof value.like === 'object' && !Array.isArray(value.like)) {
    const from = clubProgrammeParseUiDate(value.like.from);
    const to = clubProgrammeParseUiDate(value.like.to);

    if (from || to) {
      const out: any = {};
      if (from) out.$gte = from.startOf('day').toISOString();
      if (to) out.$lte = to.endOf('day').toISOString();
      return out;
    }
  }
  if (value.like && Array.isArray(value.like)) {
    return { $in: value.like };
  }
  return value;
};

export const clubProgrammeNormalizeProgrammeWhere = (where: any) => {
  const out: any = { ...(where || {}) };

  Object.keys(out).forEach(k => {
    out[k] = clubProgrammeNormalizeWhereValue(out[k]);
  });

  return out;
};



